/**
 * IN-ENGINE STEM SEPARATION (web) — the user drops ONE normal track; we separate it into
 * instruments client-side, no upload, no asking the user to know what a "stem" is.
 *
 * Pipeline: decode at 44.1k → split into N overlapped segments → N workers run Demucs v4
 * (htdemucs, 4 stems) compiled to WASM → weighted overlap-add reassembly → cached in
 * IndexedDB by content hash (a track separates ONCE per device, ever).
 *
 * Segmentation/overlap-add re-implemented in TS from free-music-demixer (MIT, © Sevag H) —
 * the proven scheme: triangular ramp weights over OVERLAP_S seconds of context per edge.
 *
 * Artifacts this module expects to be served (see public/separator/README.md):
 *   /separator/stem-worker.js + /separator/demucs_onnx_simd.{js,wasm}  (MIT, demucs.cpp)
 *   /models/htdemucs.ort.gz — the model weights (we convert Meta's MIT weights ourselves;
 *   localStorage 'syqel.sep.model' can point anywhere, e.g. an R2 bucket, for production)
 */
import type { StemId } from '@syqel/engine'

export interface SeparatedStems {
  ids: StemId[]
  sampleRate: number
  /** 2 channels per stem, [L0, R0, L1, R1, …] in `ids` order */
  channels: Float32Array<ArrayBuffer>[]
}

const STEM_ORDER: StemId[] = ['drums', 'bass', 'other', 'vocals'] // htdemucs 4s target order
const MODEL_RATE = 44100
const OVERLAP_S = 0.75
const MODEL_PATHS = ['/models/htdemucs.ort.gz', '/models/htdemucs.ort']

export type SeparateProgress = (pct: number, phase: string) => void

/* ── IndexedDB cache: a track separates once per device ─────────────────────────────── */

const DB_NAME = 'syqel-stems'
const STORE = 'stems'
const KEEP = 3 // LRU cap — stem sets are ~100-600 MB each

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

interface CacheRow {
  ids: StemId[]
  sampleRate: number
  buffers: ArrayBuffer[]
  ts: number
}

async function idb<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb()
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = fn(db.transaction(STORE, mode).objectStore(STORE))
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  } finally {
    db.close()
  }
}

async function cacheKey(file: File): Promise<string> {
  const head = await file.slice(0, 1 << 20).arrayBuffer() // first MiB + size: fast & unique enough
  const digest = await crypto.subtle.digest('SHA-256', head)
  const hex = Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex}:${file.size}`
}

async function cacheGet(key: string): Promise<SeparatedStems | null> {
  try {
    const row = await idb<CacheRow | undefined>('readonly', (s) => s.get(key) as IDBRequest<CacheRow | undefined>)
    if (!row) return null
    void idb('readwrite', (s) => s.put({ ...row, ts: Date.now() }, key)) // touch for LRU
    return { ids: row.ids, sampleRate: row.sampleRate, channels: row.buffers.map((b) => new Float32Array(b)) }
  } catch {
    return null
  }
}

async function cachePut(key: string, stems: SeparatedStems): Promise<void> {
  try {
    const row: CacheRow = {
      ids: stems.ids,
      sampleRate: stems.sampleRate,
      buffers: stems.channels.map((c) => c.buffer as ArrayBuffer),
      ts: Date.now()
    }
    await idb('readwrite', (s) => s.put(row, key))
    // LRU prune
    const keys = await idb<IDBValidKey[]>('readonly', (s) => s.getAllKeys())
    if (keys.length > KEEP) {
      const rows = await Promise.all(
        keys.map(async (k) => ({ k, row: await idb<CacheRow | undefined>('readonly', (s) => s.get(k) as IDBRequest<CacheRow | undefined>) }))
      )
      rows
        .sort((a, b) => (a.row?.ts ?? 0) - (b.row?.ts ?? 0))
        .slice(0, keys.length - KEEP)
        .forEach(({ k }) => void idb('readwrite', (s) => s.delete(k)))
    }
  } catch {
    /* quota or private mode — separation still worked, just uncached */
  }
}

/* ── segmentation + weighted overlap-add (the free-music-demixer scheme, MIT) ─────────── */

function segment(left: Float32Array, right: Float32Array, n: number, overlap: number): [Float32Array, Float32Array][] {
  const total = left.length
  const segLen = Math.ceil(total / n)
  const out: [Float32Array, Float32Array][] = []
  for (let i = 0; i < n; i += 1) {
    const start = i * segLen
    const end = Math.min(total, start + segLen)
    const L = new Float32Array(end - start + 2 * overlap)
    const R = new Float32Array(end - start + 2 * overlap)
    if (i === 0) {
      L.fill(left[0], 0, overlap)
      R.fill(right[0], 0, overlap)
    } else {
      L.set(left.subarray(start - overlap, start), 0)
      R.set(right.subarray(start - overlap, start), 0)
    }
    const tailN = Math.min(overlap, total - end)
    L.set(left.subarray(end, end + tailN), end - start + overlap)
    R.set(right.subarray(end, end + tailN), end - start + overlap)
    L.set(left.subarray(start, end), overlap)
    R.set(right.subarray(start, end), overlap)
    out.push([L, R])
  }
  return out
}

function overlapAdd(segments: Float32Array[][], total: number, overlap: number): Float32Array<ArrayBuffer>[] {
  const nT = segments[0].length
  const segLenPadded = segments[0][0].length
  const segLen = segLenPadded - 2 * overlap
  const out = Array.from({ length: nT }, () => new Float32Array(total))
  const ramp = new Float32Array(segLen)
  for (let i = 0; i < segLen; i += 1) {
    ramp[i] = Math.min(i + 1, segLen - i)
  }
  const maxW = ramp.reduce((m, x) => Math.max(m, x), 1)
  for (let i = 0; i < segLen; i += 1) ramp[i] /= maxW
  const sumW = new Float32Array(total)
  segments.forEach((seg, idx) => {
    const start = idx * segLen
    for (let t = 0; t < nT; t += 1) {
      const ch = seg[t]
      for (let i = 0; i < ch.length; i += 1) {
        const o = start + i - overlap
        if (o >= 0 && o < total) {
          out[t][o] += ramp[i % segLen] * ch[i]
          sumW[o] += ramp[i % segLen]
        }
      }
    }
  })
  for (let t = 0; t < nT; t += 1) {
    for (let i = 0; i < total; i += 1) {
      if (sumW[i] > 0) out[t][i] /= sumW[i] / nT
    }
  }
  return out
}

/* ── the separation job ──────────────────────────────────────────────────────────────── */

async function fetchModel(onProgress: SeparateProgress): Promise<ArrayBuffer> {
  const override = localStorage.getItem('syqel.sep.model')
  const candidates = override ? [override, ...MODEL_PATHS] : MODEL_PATHS
  for (const url of candidates) {
    try {
      const res = await fetch(url)
      if (!res.ok || !res.body) continue
      const total = parseInt(res.headers.get('content-length') ?? '0', 10)
      const reader = res.body.getReader()
      const parts: Uint8Array[] = []
      let loaded = 0
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        parts.push(value)
        loaded += value.length
        if (total > 0) onProgress(Math.min(25, (loaded / total) * 25), 'downloading model')
      }
      let buf = new Uint8Array(loaded)
      let o = 0
      for (const p of parts) {
        buf.set(p, o)
        o += p.length
      }
      // a literal .gz file (no transparent content-encoding) — inflate before the WASM sees it
      if (buf[0] === 0x1f && buf[1] === 0x8b) {
        const ds = new Response(new Blob([buf]).stream().pipeThrough(new DecompressionStream('gzip')))
        buf = new Uint8Array(await ds.arrayBuffer())
      }
      return buf.buffer as ArrayBuffer
    } catch {
      /* try the next source */
    }
  }
  throw new Error('separation model unavailable (see public/separator/README.md)')
}

/** Decode any audio file to stereo Float32 at the model rate. */
async function decode(file: File): Promise<{ left: Float32Array; right: Float32Array }> {
  const ctx = new AudioContext({ sampleRate: MODEL_RATE })
  try {
    const buf = await ctx.decodeAudioData(await file.arrayBuffer())
    const left = buf.getChannelData(0)
    const right = buf.numberOfChannels > 1 ? buf.getChannelData(1) : left
    return { left: new Float32Array(left), right: new Float32Array(right) }
  } finally {
    void ctx.close()
  }
}

/**
 * Separate a track into 4 instrument stems, fully client-side. Cached per device.
 * Throws when artifacts/model are unavailable — callers treat that as "no upgrade".
 */
export async function separateFile(file: File, onProgress: SeparateProgress): Promise<SeparatedStems> {
  const key = await cacheKey(file)
  const hit = await cacheGet(key)
  if (hit) {
    onProgress(100, 'cached')
    return hit
  }

  const model = await fetchModel(onProgress)
  onProgress(25, 'decoding')
  const { left, right } = await decode(file)
  const overlap = Math.floor(OVERLAP_S * MODEL_RATE)
  const nWorkers = Math.min(8, Math.max(2, (navigator.hardwareConcurrency || 4) - 2))
  const segments = segment(left, right, nWorkers, overlap)

  onProgress(28, 'separating')
  let done = 0
  const results = await Promise.all(
    segments.map(
      ([L, R], index) =>
        new Promise<{ index: number; waveforms: Float32Array[] }>((resolve, reject) => {
          const worker = new Worker('/separator/stem-worker.js')
          const fail = (why: string) => {
            worker.terminate()
            reject(new Error(why))
          }
          worker.onerror = (e) => fail(`worker error: ${e.message || 'failed to load /separator/stem-worker.js or its WASM'}`)
          worker.onmessage = (e) => {
            if (e.data.msg === 'READY') {
              if (index === 0) console.log('[syqel·sep] worker 0: model loaded into WASM, demixing…')
              worker.postMessage({ msg: 'DEMIX', index, leftChannel: L, rightChannel: R }, [L.buffer, R.buffer])
            } else if (e.data.msg === 'DONE') {
              done += 1
              onProgress(28 + (done / segments.length) * 70, `separating ${done}/${segments.length}`)
              worker.terminate()
              resolve({ index: e.data.index, waveforms: e.data.waveforms })
            } else if (e.data.msg === 'ERROR') {
              fail(String(e.data.error))
            }
          }
          // the model buffer is structured-cloned per worker (each needs its own copy anyway)
          worker.postMessage({ msg: 'LOAD', model })
        })
    )
  )

  results.sort((a, b) => a.index - b.index)
  const channels = overlapAdd(results.map((r) => r.waveforms), left.length, overlap)
  const stems: SeparatedStems = { ids: STEM_ORDER, sampleRate: MODEL_RATE, channels }
  onProgress(99, 'caching')
  await cachePut(key, stems)
  onProgress(100, 'done')
  return stems
}
